// ReservationDetail.tsx
import React, { useState, useMemo } from 'react';
import { useFetchReservationDetailsQuery, usePatchReservationDetailsMutation, useGetAvailableUnitsDataQuery } from '../../../api/endpoints/apiConfig'; // Update with your actual path
import { AccordionSummary, Link, AccordionDetails, Typography, Stack, Button, Grid2, List, ListItem, ListItemText, Divider, Modal, Box } from '@mui/material';
import CustomAccordion from '../../../components/Accordian/accordian';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Promotion } from '../../../models/Promotions';
import { AircallNofity } from '../../../components/Common/notify';
import { useNavigate } from 'react-router-dom';
import { addId } from '../../../store/reducers/aircallTagSlice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
type Props = {
    facilityId: number;
    dealId: number;
    onUpdate: () => void;
};
const ReservationDetail: React.FC<Props> = ({ facilityId, dealId, onUpdate }) => {
    // Fetch reservation details with numeric IDs
    const { data: reservationDetails, isLoading, isError } = useFetchReservationDetailsQuery({
        facilityId,
        dealId,
    });
    const { data: responseData } = useGetAvailableUnitsDataQuery(facilityId);
    const [patchReservationDetails] = usePatchReservationDetailsMutation();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // Parse facilityData and create promoMap
    const facilityData = JSON.parse(sessionStorage.getItem('facilityData') || '[]');
    const promotions: Promotion[] = facilityData[0]?.promotions || [];
    const promoMap: Record<string, string> = promotions.reduce((map: Record<string, string>, promo: Promotion) => {
        map[promo.external_id] = promo.name;
        return map;
    }, {});

    // Unit price from matching unit type price list (exact or price-changed match) - must be before early returns
    const unitPriceToDisplay = useMemo(() => {
        const availableUnits = responseData?.available_units ?? [];
        const dealUnit = reservationDetails?.deal?.unit;
        if (!dealUnit) return reservationDetails?.deal?.quoted_price ?? 0;

        const findMatch = (includePrice: boolean) =>
            availableUnits.find((u: { width?: number; length?: number; inside?: number; drive_up?: number; climate?: number; air_cooled?: number; heated?: number; elevator?: number; stair_access?: number; external_type_id?: string; price?: number }) => {
                const wMatch = Number(u.width ?? 0) === Number(dealUnit.width ?? 0);
                const lMatch = Number(u.length ?? 0) === Number(dealUnit.length ?? 0);
                const typeMatch = String(u.external_type_id ?? '') === String(dealUnit.external_unit_type_id ?? '');
                const insideMatch = Number(u.inside ?? 0) === Number(dealUnit.is_inside ?? 0);
                const driveUpMatch = Number(u.drive_up ?? 0) === Number(dealUnit.is_drive_up ?? 0);
                const climateMatch = Number(u.climate ?? 0) === Number(dealUnit.is_climate ?? 0);
                const airCooledMatch = Number(u.air_cooled ?? 0) === Number(dealUnit.is_air_cooled ?? 0);
                const heatedMatch = Number(u.heated ?? 0) === Number(dealUnit.is_heated ?? 0);
                const elevatorMatch = Number(u.elevator ?? 0) === Number(dealUnit.is_elevator ?? 0);
                const stairAccessMatch = Number(u.stair_access ?? 0) === Number(dealUnit.is_stair_access ?? 0);
                const priceMatch = !includePrice || Math.abs(Number(u.price ?? 0) - Number(reservationDetails?.deal?.quoted_price ?? 0)) < 0.01;
                return wMatch && lMatch && typeMatch && insideMatch && driveUpMatch && climateMatch && airCooledMatch && heatedMatch && elevatorMatch && stairAccessMatch && priceMatch;
            });

        const exactMatch = findMatch(true);
        const priceChangedMatch = findMatch(false);
        const effectiveMatch = exactMatch ?? priceChangedMatch;
        return effectiveMatch?.price ?? reservationDetails?.deal?.quoted_price ?? 0;
    }, [responseData, reservationDetails]);

    if (isLoading) return <Typography>Loading...</Typography>;
    if (isError) return <Typography>Error loading reservation details.</Typography>;

    // Phone number formatting
    const formatPhoneNumber = (phone: string): string => {
        if (phone.length < 10) return 'Invalid Number'; // Check for valid phone number length
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    };

    const formatUnitFeatures = (unit: {
        floor?: number;
        inside?: number;
        is_inside?: number;
        drive_up?: number;
        is_drive_up?: number;
        climate?: number;
        is_climate?: number;
        air_cooled?: number;
        is_air_cooled?: number;
        heated?: number;
        is_heated?: number;
        elevator?: number;
        is_elevator?: number;
        stair_access?: number;
        is_stair_access?: number;
    } | null | undefined): string => {
        if (!unit) return 'No unit details available';
        const features: string[] = [];
        const floor = unit.floor;
        const inside = unit.inside ?? unit.is_inside;
        const driveUp = unit.drive_up ?? unit.is_drive_up;
        const climate = unit.climate ?? unit.is_climate;
        const airCooled = unit.air_cooled ?? unit.is_air_cooled;
        const heated = unit.heated ?? unit.is_heated;
        const elevator = unit.elevator ?? unit.is_elevator;
        const stairAccess = unit.stair_access ?? unit.is_stair_access;
        if (floor === 1) features.push('First Floor');
        if (floor !== undefined && floor > 1) features.push('Upper');
        if (inside) features.push('Inside');
        if (driveUp) features.push('Drive Up');
        if (climate) features.push('Climate');
        if (airCooled) features.push('Air Cooled');
        if (heated) features.push('Heated');
        if (elevator) features.push('Elevator Access');
        if (stairAccess) features.push('Stair Access');
        return features.join(', ') || 'No unit details available';
    };
    const phnumber = formatPhoneNumber(reservationDetails?.deal?.phone ?? '');
    const dealPromoId = String(reservationDetails?.deal?.external_promotion_id ?? '').trim();
    const unitPromoName = (responseData?.available_units ?? [])
        .flatMap((unit) => unit.promotions ?? [])
        .find((promo) => String(promo.id ?? '').trim() === dealPromoId)
        ?.name;
    const promotionName = promoMap[dealPromoId] || unitPromoName || '';

    const handleOpen = () => { setOpen(true) };
    const handleClose = () => { setOpen(false) };
    const CancelConfirmation = (e: React.MouseEvent) => {
        e.preventDefault();
        handleOpen();
    };
    // tag api call 
    const SettingData = sessionStorage.getItem('setting');
    const Settings = SettingData ? JSON.parse(SettingData) : [];
    const IsLead = Settings.is_deal_type_lead_enabled;
    const tagdatasave = () => {
        // Retrieve and parse tag data from sessionStorage
        const tagdata = sessionStorage.getItem('tagsdata');
        const tagdataObject = tagdata ? JSON.parse(tagdata) : {};
        const tags = tagdataObject.tags || [];
        // Find the relevant tags for Reservation and Lead
        const Reservationdetails = tags.find((tag: { name: string }) => tag.name === 'Reservation - Canceled');
        const IsLeaddata = tags.find((tag: { name: string }) => tag.name === 'Lead - Canceled');
        // Dispatch to Redux store if tags are found
        if (Reservationdetails && Reservationdetails.id) {
            dispatch(addId(Reservationdetails.id)); // Dispatch to Redux store
        }
        if (IsLead && IsLeaddata && IsLeaddata.id) {
            dispatch(addId(IsLeaddata.id)); // Dispatch to Redux store
        }
    };
    const handleCancel = async () => {
        if (!reservationDetails) return;
        handleClose();
        try {
            // Destructure reservationDetails.deal
            var agentName = sessionStorage.getItem('FirstName') + ' ' + sessionStorage.getItem('LastName');
            const updatedReservation = {
                first_name: reservationDetails.deal?.first_name,
                last_name: reservationDetails.deal?.last_name,
                email: reservationDetails.deal?.email,
                phone: reservationDetails.deal?.phone,
                quoted_rate: reservationDetails.deal?.quoted_price ?? 0,
                type: reservationDetails.deal?.type ?? 'N/A',
                move_in_date: reservationDetails?.deal.move_in_date ?? 'N/A',
                followup_date: reservationDetails.deal.followup_at ?? 'N/A',
                external_unit_id: reservationDetails.deal.external_unit_id,
                external_promotion_id: reservationDetails.deal.external_promotion_id,
                external_product_type_id: '1763',
                status: 'cancel',
                agent: agentName,
                //external_occupant_id: external_occupant_id ?? 'N/A',
                //notes: null,
            };
            await patchReservationDetails({
                facilityId,
                dealId,
                body: updatedReservation,
            }).unwrap();
            AircallNofity({ type: 'success', Msg: 'Reservation Canceled Successfully' });
            tagdatasave();
            onUpdate();
            navigate(`/Dashboard/${facilityId}`);

        } catch (error) {
            console.error('Failed to update the reservation Cancel:', error);
            AircallNofity({ type: 'error', Msg: 'Failed to Reservation Canceled ' });
        }
    };
    const style = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };
    return (
        <>
            <CustomAccordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="reservation-detail"
                >
                    <Stack direction={'row'} useFlexGap justifyContent={'space-between'} flex={1} alignItems={'center'}>
                        <Typography variant="h3" color="text.secondary">Reservation Details</Typography>

                    </Stack>
                </AccordionSummary>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="flex-start"
                    sx={{ flexGrow: 1, width: '100%', marginTop: '10px' }}
                >
                    <Link
                        component="button"
                        type="button"
                        onClick={CancelConfirmation}
                        sx={{
                            marginRight: 2,
                            textDecoration: 'none',
                            color: 'error.main',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            font: 'inherit',
                            padding: 0,
                        }}
                    >
                        Cancel
                    </Link>
                </Stack>
                <AccordionDetails>
                    <Grid2 container spacing={2} columns={12}>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <Typography mb={1} variant='h3' color='text.secondary'>Tenant Information</Typography>
                            <List>
                                {reservationDetails?.deal?.first_name ? (
                                    <Stack direction={'row'} spacing={2} useFlexGap justifyContent={'space-between'}>
                                        <ListItem>
                                            <ListItemText
                                                primary={'First Name'}
                                                secondary={reservationDetails.deal.first_name}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={'Last Name'}
                                                secondary={reservationDetails?.deal?.last_name ?? 'N/A'}
                                            />
                                        </ListItem>
                                    </Stack>
                                ) : (
                                    <Typography variant="body1" color="error">
                                        PMS_RESERVATION_NOT_FOUND
                                    </Typography>
                                )}
                                {reservationDetails?.deal?.first_name && (
                                    <Stack direction={'row'} spacing={2} useFlexGap justifyContent={'space-between'}>
                                        <ListItem>
                                            <ListItemText
                                                primary="Phone Number"
                                                secondary={phnumber}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={'Email'}
                                                secondary={reservationDetails?.deal?.email ?? 'N/A'}
                                            />
                                        </ListItem>
                                    </Stack>
                                )}
                            </List>
                        </Grid2>
                        <Divider orientation="vertical" flexItem />
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <Typography mb={1} variant='h3' color='text.secondary'>Reservation Information </Typography>
                            <Grid2 container spacing={2} columns={12}>
                                {reservationDetails?.deal?.first_name ? (
                                    <>
                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary={'Type'} secondary={reservationDetails?.deal?.type ?? 'N/A'} />
                                                </ListItem>
                                            </List>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary={'Unit Size'} secondary={reservationDetails?.deal?.unit_dimensions ?? 'N/A'} />
                                                </ListItem>
                                            </List>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary={'Current Deal Price'} secondary={`$${reservationDetails?.deal?.quoted_price?.toFixed(2) ?? '0.00'}`} />
                                                </ListItem>
                                            </List>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary={'Promotion'} 
                                                    secondary={promotionName?.trim() ? promotionName : "No Promo"}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, md: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Features"
                                                        secondary={formatUnitFeatures(reservationDetails?.deal?.unit)}
                                                    />
                                                </ListItem>
                                            </List>

                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                            <List>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Unit Price"
                                                        secondary={`$${Number(unitPriceToDisplay ?? 0).toFixed(2)}`}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 8}}>

                                        <List>
                                                {/* <ListItem>
                                                    <ListItemText primary={'Unit Number'} secondary={reservationDetails?.deal.unit.name} />
                                                </ListItem> */}
                                            </List>

                                        </Grid2>
                                    </>
                                ) : (
                                    <Typography variant="body1" color="error" align="center">
                                        PMS_RESERVATION_NOT_FOUND
                                    </Typography>
                                )}
                            </Grid2>
                        </Grid2>

                        <Divider orientation="vertical" flexItem />
                        <Grid2 size={{ xs: 12, md: 3.5 }}>
                            <Grid2 container spacing={2} columns={12} size={{ xs: 12, md: 3.5 }}>
                                {reservationDetails?.deal?.first_name ? (
                                    <>
                                        <Typography variant="h3" color="text.secondary" gutterBottom>
                                            Date Information
                                        </Typography>
                                        <Grid2 container spacing={2} direction="row">
                                            {/* Move-In Date */}
                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <List>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Move-In Date"
                                                            secondary={
                                                                reservationDetails?.deal?.move_in_date
                                                                    ? new Date(reservationDetails?.deal?.move_in_date).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        timeZone: 'UTC',
                                                                    })
                                                                    : 'N/A'
                                                            }
                                                        />
                                                    </ListItem>
                                                </List>
                                            </Grid2>
                                            {/* Follow-Up Date */}
                                            <Grid2 size={{ xs: 12, md: 6 }}>
                                                <List>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Follow-Up Date"
                                                            secondary={
                                                                reservationDetails?.deal?.followup_at
                                                                    ? new Date(reservationDetails?.deal?.followup_at).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        timeZone: 'UTC',
                                                                    })
                                                                    : 'N/A'
                                                            }
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        />
                                                    </ListItem>
                                                </List>
                                            </Grid2>
                                        </Grid2>
                                    </>
                                ) : (
                                    <Typography variant="body1" color="error" align="center">
                                        PMS_RESERVATION_NOT_FOUND
                                    </Typography>
                                )}
                            </Grid2>

                        </Grid2>
                    </Grid2>
                </AccordionDetails>
            </CustomAccordion>
            <div>
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box
                        sx={{
                            ...style, // Your existing styles
                            width: '500px', // Increase the width
                            maxWidth: '80%', // Ensure it’s responsive on smaller screens
                            padding: 4, // Optional: add padding for better layout
                        }}
                    >
                        <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                            Are You Sure You Want To Cancel This Reservation?
                        </Typography>
                        {/* Center-aligned button container */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button onClick={handleCancel} variant="contained">
                                Yes
                            </Button>
                            <Button onClick={handleClose} variant="contained">
                                No
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </div>
        </>
    );
};
export default ReservationDetail;